import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { PageHeader, TWDateRangePicker } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import connectApi from '../../../services/connectApi';
import storeApi from '../../../services/storeApi';
import { DATE_OPTIONS } from '../../dashboard/utils/date-presets';
import FinDropdown from '../../connect/finance/fin-dropdown';
import FinButton from '../../connect/finance/fin-button';
import { FinTableSkeleton } from '../../connect/finance/fin-skeleton';
import COPY from '../../connect/finance/connect-finance-copy';
import { useFinanceSection } from '../section/finance-section-context';
import { downloadCsv } from '../csv';
import useFinancePeriod, { CUSTOM_RANGE, DEFAULT_PERIOD_PRESET, resolveFinanceRange } from './use-finance-period';
import { buildOrderReportColumns, columnStyle, visibleOptionalColumns } from './order-report-columns';
import FinInfoPopover from '../../connect/finance/fin-info-popover';
import OrderReportPager from './order-report-pager';
import FinancePageShell from './finance-page-shell';

const ALL_STORES = '';
const PAGE_SIZE = 22;

// The restaurant's statement, order by order (spec 2026-09-22 §7, frame 3a):
// one grid table, the store and period controls beside the title, the mock's
// pager inside the card. No tabs — a refunded order is an ordinary row whose
// Refund column is non-zero — and no summary cards. Every figure comes from
// the server; nothing is summed here, and since 2026-09-22 nothing is paged
// here either: 22 rows a page is a server contract, so the footer can count
// the whole filter rather than the rows on screen.
const OrderReports = () => {
  const { companyId } = useFinanceSection();
  const [storeId, setStoreId] = useState(ALL_STORES);
  const [stores, setStores] = useState([]);
  const [page, setPage] = useState(1);

  // Whose day the period is cut on: the chosen store's, else the company's
  // oldest store's — the rule ConnectAccountService.PrimaryStoreAsync uses.
  // Undefined until the list lands, which falls back to the reader's clock.
  const zone = useMemo(() => {
    const chosen = storeId ? stores.find((store) => store.id === storeId) : null;
    const primary = [...stores].sort((a, b) => new Date(a.createdate || 0) - new Date(b.createdate || 0))[0];
    return (chosen || primary)?.timezoneid || undefined;
  }, [storeId, stores]);

  const { period, setPeriod, query } = useFinancePeriod(zone);
  // Gates the first read on the store list, success or failure, so that read
  // already uses the store's calendar — never the browser's clock. Without
  // this, `zone` starts undefined, the load effect below fires once on the
  // browser's "now", and again moments later once the store list lands and
  // `zone`/`query` change: a phantom read the reader briefly sees replaced.
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [section, setSection] = useState({ data: null, error: '', loading: true });
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Cleans the URL on mount: an old ?tab=refunds link is dropped (Refunds is
  // neither a page nor a tab any more), and an unrecognised ?period= —
  // readFinancePeriod already answers it with the default — is written back
  // so a shared or bookmarked link ends up matching what the dropdown itself
  // shows chosen, rather than displaying a preset nothing has selected.
  useEffect(() => {
    const hasTab = searchParams.has('tab');
    const rawPeriod = searchParams.get('period');
    const periodKnown = !rawPeriod || DATE_OPTIONS.some((option) => option.value === rawPeriod);
    if (!hasTab && periodKnown) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    if (!periodKnown) next.set('period', DEFAULT_PERIOD_PRESET);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const copy = COPY.restaurant;
  const pageCopy = copy.sections.orderReports;

  const load = useCallback(async (nextPage) => {
    requestId.current += 1;
    const id = requestId.current;
    setSection((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const data = connectApi.toOrderReports(
        await connectApi.getOrderReports(
          companyId, query.from, query.to, storeId || undefined, nextPage, PAGE_SIZE,
        ),
      );
      if (!mounted.current || id !== requestId.current) return;
      if (!data) throw new Error('order-reports');
      setSection({ data, error: '', loading: false });
    } catch {
      if (!mounted.current || id !== requestId.current) return;
      // Never zeros over an error: an empty statement and a refused read
      // look identical to a restaurant, and only one of them is true.
      setSection({ data: null, error: pageCopy.error, loading: false });
    }
  }, [companyId, pageCopy, query.from, query.to, storeId]);

  // A store or period change is a different list, so it always starts at the
  // first page — and both effects below are one read, never two. It also
  // outdates any export notice still on screen: a failure over the old
  // filter says nothing about whether the new one would export cleanly.
  useEffect(() => {
    if (!companyId || !storesLoaded) return;
    setPage(1);
    setNotice('');
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, query.from, query.to, storeId, storesLoaded]);

  const goToPage = (next) => {
    setPage(next);
    load(next);
  };

  useEffect(() => {
    if (!companyId) return;
    let active = true;
    (async () => {
      try {
        const res = await storeApi.list('companyid=' + companyId);
        if (active) setStores(res?.data?.data || []);
      } catch {
        if (active) setStores([]);
      } finally {
        if (active) setStoresLoaded(true);
      }
    })();
    return () => { active = false; };
  }, [companyId]);

  const rows = useMemo(() => section.data?.rows || [], [section.data]);
  const totalCount = section.data?.totalCount || 0;
  // The API decides the optional columns over the whole filter (hascrv /
  // hascustomerfee → `optional`); only an older API leaves the page to scan
  // its own 22 rows. Either way the grid and the CSV agree.
  const optional = section.data?.optional;
  const columns = useMemo(
    () => buildOrderReportColumns(optional || visibleOptionalColumns(rows)),
    [optional, rows],
  );
  const style = useMemo(() => columnStyle(columns), [columns]);
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const first = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, totalCount);
  const shown = rows;

  // The order number stays pinned while the fourteen columns scroll
  // sideways on a phone (owner, 2026-09-22). The negative margin and padding
  // carry its background over the row's own 20px gutter, so nothing shows
  // through beside it.
  const cellCls = (column) => cn(
    'min-w-0',
    column.align === 'right' && 'text-right',
    column.key === 'orderNumber' && 'sticky left-0 z-10 -ml-5 bg-fin-surface pl-5',
  );

  // The server's file, over the whole filter — not the 22 rows on screen,
  // and not a client-side rebuild of columns the server already decides
  // (spec 2026-09-22 §7). A failed download is a notice above the card, not
  // a dead button: the statement itself is still readable.
  //
  // The NAME is ours, per spec §7: the response carries a constant
  // `filename="order-reports.csv"`, and only this side knows which window the
  // reader is looking at. It is named from resolveFinanceRange's own day
  // values — the store's calendar days — and NOT from `query.from`/`query.to`,
  // which are UTC instants (`toPeriodQuery`'s `endOf('day')` in the store's
  // zone). For a zone behind UTC, that instant can fall on the next UTC
  // calendar date, so reading it back with `dayjs.utc(...).format(...)`
  // named the file a day later than the days it actually covers.
  const exportCsv = async () => {
    setExporting(true);
    setNotice('');
    try {
      const text = await connectApi.exportOrderReports(companyId, query.from, query.to, storeId || undefined);
      const range = resolveFinanceRange(period, zone);
      downloadCsv(text, `order-reports-${range.from.format('YYYY-MM-DD')}-${range.to.format('YYYY-MM-DD')}.csv`);
    } catch {
      setNotice(copy.exportFailed);
    } finally {
      if (mounted.current) setExporting(false);
    }
  };

  return (
    <FinancePageShell>
      <PageHeader
        title={pageCopy.title}
        description={pageCopy.description}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <FinDropdown
              mode="radio"
              size="md"
              aria-label="Store"
              value={storeId}
              options={[{ value: ALL_STORES, label: 'All stores' }, ...stores.map((s) => ({ value: s.id, label: s.name }))]}
              onChange={setStoreId}
            />
            {/* One dropdown, the dashboard's presets, no stepper (spec
                2026-09-22 §7). ConnectPeriodPicker is not mounted here any
                more; it still serves the admin's Settlement screen. */}
            <FinDropdown
              mode="radio"
              size="md"
              aria-label="Period"
              value={period.preset}
              options={DATE_OPTIONS}
              onChange={(preset) => setPeriod({ preset, custom: period.custom })}
            />
            {period.preset === CUSTOM_RANGE ? (
              <div className="w-[260px]">
                <TWDateRangePicker
                  label="Custom Range"
                  labelIcon={CalendarDaysIcon}
                  size="middle"
                  value={[period.custom.from || null, period.custom.to || null]}
                  onChange={(range) =>
                    setPeriod(
                      range && range[0] && range[1]
                        ? { preset: CUSTOM_RANGE, custom: { from: range[0], to: range[1] } }
                        : { preset: CUSTOM_RANGE, custom: { from: null, to: null } },
                    )
                  }
                />
              </div>
            ) : null}
            <FinButton disabled={exporting} onClick={exportCsv}>
              {exporting ? 'Exporting…' : 'Export'}
            </FinButton>
          </div>
        }
      />

      {notice ? <p role="alert" className="text-[13px] text-fin-red">{notice}</p> : null}

      <section data-testid="order-report-table" className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
        {section.error ? (
          <div className="flex min-h-[320px] items-center justify-center gap-3 px-5 text-center">
            <p className="text-[13px] text-fin-red">{section.error}</p>
            {/* load takes a page NUMBER, not the click's SyntheticEvent —
                onClick={load} would hand it the event as `nextPage` and the
                request would ask the server for page "[object Object]". */}
            <FinButton onClick={() => load(page)}>{copy.retry}</FinButton>
          </div>
        ) : section.loading && !section.data ? (
          <div className="min-h-[320px]">
            <FinTableSkeleton rows={8} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: style.minWidth }}>
              <div role="table" aria-label="Transactions">
                <div
                  role="row"
                  className="grid gap-x-2.5 border-b border-fin-border px-5 py-4 text-[13.5px] font-bold text-fin-ink"
                  style={{ gridTemplateColumns: style.gridTemplateColumns }}
                >
                  {columns.map((column) => (
                    <div key={column.key} role="columnheader" className={cellCls(column)}>
                      {column.info ? (
                        // One line, heading then icon (owner, 2026-10-06):
                        // the explanation used to be part of the heading
                        // and wrapped it.
                        <span className={cn('inline-flex items-center gap-1 whitespace-nowrap', column.align === 'right' && 'justify-end')}>
                          {column.label}
                          <FinInfoPopover ariaLabel={`About ${column.label}`} text={column.info} />
                        </span>
                      ) : column.label}
                    </div>
                  ))}
                </div>
                {/* rowgroup, not a bare div: ARIA owns row/rowgroup only, and
                    the 320px body starts under the headers (spec 2026-09-22
                    §3.3). */}
                <div role="rowgroup" className={shown.length > 0 ? 'min-h-[320px]' : undefined}>
                  {shown.map((row, index) => (
                    <div
                      key={row.orderId || `${row.orderNumber}-${index}`}
                      role="row"
                      className={cn('grid items-center gap-x-2.5 px-5 py-3 text-[13px]', index < shown.length - 1 && 'border-b border-fin-hair')}
                      style={{ gridTemplateColumns: style.gridTemplateColumns }}
                    >
                      {columns.map((column) => (
                        <div key={column.key} role="cell" className={cellCls(column)}>{column.render(row)}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              {/* Sibling of role=table, not a child: a <p> is disallowed
                  content under table/grid (spec 2026-09-21 review, final
                  wave item 1/6/16). */}
              {rows.length === 0 ? (
                <div className="flex min-h-[320px] items-center justify-center px-5 text-center">
                  <p className="text-[13px] text-fin-muted">{pageCopy.empty}</p>
                </div>
              ) : null}
            </div>
          </div>
        )}
        {!section.error && totalCount > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <p className="text-[13px] text-fin-muted">{`Showing ${first}–${last} of ${totalCount} orders`}</p>
            <OrderReportPager page={page} pageCount={pageCount} onChange={goToPage} />
          </div>
        ) : null}
      </section>
    </FinancePageShell>
  );
};

export default OrderReports;
